import React, { useState, useEffect } from 'react';
import { getHousekeeperAssessment, getAssessmentDetails, initAssessmentDetails, updateAssessmentReview, getHousekeeperById, getAssessmentCriteria, getAssessmentRoleConfig } from '../api/housekeeperApi';
import './AssessmentReviewPage.css';

// 默认配置（育婴师）
const DEFAULT_ROLE = '育婴师';

// 计算总分的纯函数
function calculateTotalFromState(mainScores, detailScores, groupedDetails, roleConfig) {
  const certCap = roleConfig?.certificate_cap || 3;
  const certItems = groupedDetails.certificate || [];
  const certSum = certItems.reduce((sum, d) => sum + (parseFloat(detailScores[d.id]) || 0), 0);
  const certTotal = Math.min(certSum, certCap);

  const skillTotal = (roleConfig?.skill_categories || []).reduce((sum, cat) => {
    const items = groupedDetails[cat.key] || [];
    return sum + items.reduce((s, d) => s + (parseFloat(detailScores[d.id]) || 0), 0);
  }, 0);

  return Math.round((
    (mainScores.age_score || 0) +
    (mainScores.education_score || 0) +
    (mainScores.education_bonus || 0) +
    (mainScores.work_experience_score || 0) +
    certTotal +
    (mainScores.appearance_score || 0) +
    (mainScores.etiquette_score || 0) +
    (mainScores.communication_score || 0) +
    (mainScores.logic_score || 0) +
    (mainScores.comprehensive_score || 0) +
    skillTotal +
    (mainScores.special_skill_score || 0) +
    (mainScores.mixed_work_score || 0)
  ) * 100) / 100;
}

// ===== 评分规则浮窗组件 =====
function ScoringRulePopover({ visible, rule, onClose }) {
  if (!visible) return null;
  return (
    <div className="popover-overlay" onClick={onClose}>
      <div className="popover-content" onClick={e => e.stopPropagation()}>
        <div className="popover-header">
          <span>评分规则</span>
          <button className="popover-close" onClick={onClose}>X</button>
        </div>
        <div className="popover-body">
          <pre>{rule}</pre>
        </div>
      </div>
    </div>
  );
}

// ===== 主组件 =====
export default function AssessmentReviewPage({ id, navigateTo }) {
  const [housekeeper, setHousekeeper] = useState({});
  const [roleConfig, setRoleConfig] = useState(null); // 角色测评配置（从后端获取）
  const [assessment, setAssessment] = useState({});
  const [details, setDetails] = useState([]);
  const [criteriaMap, setCriteriaMap] = useState({}); // category -> items (含scoring_rule)
  const [abilitySalary, setAbilitySalary] = useState('');
  const [mainScores, setMainScores] = useState({});
  const [detailScores, setDetailScores] = useState({});
  const [certificateRadios, setCertificateRadios] = useState({});
  const [medicalProfession, setMedicalProfession] = useState(null);
  const [interviewSelections, setInterviewSelections] = useState({
    appearance: -1, etiquette: -1, communication: -1, logic: -1, comprehensive: -1,
  });
  const [loading, setLoading] = useState(true);
  const [popover, setPopover] = useState({ visible: false, rule: '' });

  useEffect(() => { if (id) loadData(); }, [id]);

  // 从年龄评分规则计算分数
  const calcAgeScore = (age, ageRule) => {
    if (!age || !ageRule) return 0;
    // 解析规则文本，例如 "28岁-42岁：5分；24岁-27岁或43岁-45岁：3分；小于24岁或大于45岁：1分"
    const rules = [];
    const parts = ageRule.split(/[；;]/);
    for (const part of parts) {
      // 匹配 "28岁-42岁：5分" 格式
      const rangeMatch = part.match(/(\d+)岁[-~](\d+)岁[：:]\s*(\d+)分/);
      if (rangeMatch) {
        rules.push({ min: parseInt(rangeMatch[1]), max: parseInt(rangeMatch[2]), score: parseInt(rangeMatch[3]) });
      }
      // 匹配 "小于24岁：1分" 格式
      const lessMatch = part.match(/小于(\d+)岁[：:]\s*(\d+)分/);
      if (lessMatch) {
        rules.push({ min: 0, max: parseInt(lessMatch[1]) - 1, score: parseInt(lessMatch[2]) });
      }
      // 匹配 "大于45岁：1分" 格式
      const greaterMatch = part.match(/大于(\d+)岁[：:]\s*(\d+)分/);
      if (greaterMatch) {
        rules.push({ min: parseInt(greaterMatch[1]) + 1, max: 999, score: parseInt(greaterMatch[2]) });
      }
    }
    for (const rule of rules) {
      if (age >= rule.min && age <= rule.max) return rule.score;
    }
    return 0;
  };

  // 从学历评分规则计算分数 — 统一函数
  const calcEducationScore = (education, eduRule) => {
    if (!education || !eduRule) return 0;
    const e = String(education);

    // 直接匹配规则文本中的学历模式
    // 支持 "本科及以上5分" 或 "本科及以上：5分" 格式
    const patterns = [
      { regex: /本科及以上[：:]?\s*(\d+)\s*分/, keys: ['博士', '硕士', '本科'] },
      { regex: /大专[：:]?\s*(\d+)\s*分/, keys: ['大专'] },
      { regex: /高中\/中专[：:]?\s*(\d+)\s*分/, keys: ['高中', '中专'] },
      { regex: /初中[：:]?\s*(\d+)\s*分/, keys: ['初中'] },
    ];

    for (const pattern of patterns) {
      if (pattern.keys.some(k => e.includes(k))) {
        const m = eduRule.match(pattern.regex);
        if (m) return parseInt(m[1]);
      }
    }
    return 0;
  };

  const loadData = async () => {
    setLoading(true);
    let rcRes = null; // 在外层声明，以便后续使用
    try {
      // 加载阿姨信息
      const hkRes = await getHousekeeperById(id);
      const hk = hkRes.data;
      setHousekeeper(hk);
      setAbilitySalary(hk.ability_salary ? String(hk.ability_salary) : '');

      // 根据角色从后端加载配置
      const role = hk.role || DEFAULT_ROLE;
      try {
        rcRes = await getAssessmentRoleConfig(role);
        if (rcRes.data) {
          setRoleConfig(rcRes.data);
        } else {
          console.error('Failed to load role config from backend');
          alert('无法加载测评配置，请检查后端数据');
          setLoading(false);
          return;
        }
      } catch (e) {
        console.error('Failed to load role config', e);
        alert('无法加载测评配置，请检查后端服务');
        setLoading(false);
        return;
      }

      // 加载评分标准（用于?号浮窗，按角色）
      let cMap = {};
      try {
        const criteriaRes = await getAssessmentCriteria(role);
        if (criteriaRes.data) {
          Object.keys(criteriaRes.data).forEach(cat => {
            cMap[cat] = criteriaRes.data[cat].items || [];
          });
        }
        setCriteriaMap(cMap);
      } catch (e) { console.error('criteria load err', e); }

      // 加载测评数据
      const assessmentRes = await getHousekeeperAssessment(id);
      const a = assessmentRes.data;
      setAssessment(a);

      if (a && a.id) {
        // 从 rcRes.data 中获取学历评分规则
        const eduRule = rcRes.data.education_scoring_rule;
        const eduScore = calcEducationScore(hk.education, eduRule);

        // 初始化所有分数到 mainScores，确保页面计算使用统一数据源
        setMainScores({
          age_score: parseFloat(a.age_score) || 0,
          education_score: eduScore,  // 使用重新计算的学历分数
          education_bonus: parseFloat(a.education_bonus) || 0,
          work_experience_score: parseFloat(a.work_experience_score) || 0,
          appearance_score: parseFloat(a.appearance_score) || 0,
          etiquette_score: parseFloat(a.etiquette_score) || 0,
          communication_score: parseFloat(a.communication_score) || 0,
          logic_score: parseFloat(a.logic_score) || 0,
          comprehensive_score: parseFloat(a.comprehensive_score) || 0,
          special_skill_score: parseFloat(a.special_skill_score) || 0,
          mixed_work_score: parseFloat(a.mixed_work_score) || 0,
        });
        // 从 education_bonus 反推"是否医护专业"的选中状态
        // education_bonus=5 表示"是"，=0 表示"否"
        const eduBonus = parseFloat(a.education_bonus) || 0;
        setMedicalProfession(eduBonus > 0 ? 5 : 0);

        // 面试表现反推选项（从已保存的分数反推选中索引）
        // 注意：这里使用 rcRes.data 而不是 roleConfig 状态，因为状态更新是异步的
        const initSel = {};
        ['appearance', 'etiquette', 'communication', 'logic', 'comprehensive'].forEach(f => {
          const score = parseFloat(a[f + '_score']) || 0;
          const opts = rcRes.data?.interview_options?.[f];
          const idx = opts ? opts.findIndex(o => o.score === score) : -1;
          initSel[f] = idx >= 0 ? idx : -1;
        });
        setInterviewSelections(initSel);

        // 加载细项
        let detailsRes = await getAssessmentDetails(id);
        let dList = detailsRes.data.details || [];
        if (dList.length === 0) {
          await initAssessmentDetails(id);
          detailsRes = await getAssessmentDetails(id);
          dList = detailsRes.data.details || [];
        }
        setDetails(dList);

        const ds = {};
        const cr = {};
        dList.forEach(d => {
          ds[d.id] = parseFloat(d.score) || 0;
          if (d.category === 'certificate' && d.score_type === 'radio') {
            cr[d.id] = d.score > 0 ? 1 : 0;
          }
        });
        setDetailScores(ds);
        setCertificateRadios(cr);

        // 页面加载完成后，计算总分并与后端 total_score 对比
        // 构建 groupedDetails
        const tempGroupedDetails = {};
        dList.forEach(d => {
          if (!tempGroupedDetails[d.category]) tempGroupedDetails[d.category] = [];
          tempGroupedDetails[d.category].push(d);
        });

        const calculatedTotal = calculateTotalFromState(
          {
            age_score: parseFloat(a.age_score) || 0,
            education_score: parseFloat(a.education_score) || 0,
            education_bonus: parseFloat(a.education_bonus) || 0,
            work_experience_score: parseFloat(a.work_experience_score) || 0,
            appearance_score: parseFloat(a.appearance_score) || 0,
            etiquette_score: parseFloat(a.etiquette_score) || 0,
            communication_score: parseFloat(a.communication_score) || 0,
            logic_score: parseFloat(a.logic_score) || 0,
            comprehensive_score: parseFloat(a.comprehensive_score) || 0,
            special_skill_score: parseFloat(a.special_skill_score) || 0,
            mixed_work_score: parseFloat(a.mixed_work_score) || 0,
          },
          ds,
          tempGroupedDetails,
          rcRes.data
        );
        const backendTotal = parseFloat(a.total_score) || 0;
        if (Math.abs(calculatedTotal - backendTotal) > 0.01) {
          alert(`页面得分(${calculatedTotal})和后台得分(${backendTotal})不一致，请检查`);
        }
      }
    } catch (e) { console.error('Failed to load', e); }
    setLoading(false);
  };

  // 获取评分规则文本
  const getScoringRule = (category, itemOrder) => {
    const items = criteriaMap[category];
    if (!items) return '';
    const item = items.find(i => i.item_order === itemOrder);
    return item ? (item.scoring_rule || '') : '';
  };

  // 获取个人资历类的评分规则
  const getPersonalRule = (type) => {
    const items = criteriaMap['personal_qualification'];
    if (!items) return '';
    const orderMap = { age: 1, education: 2, medical: 3, work_exp: 4 };
    const item = items.find(i => i.item_order === orderMap[type]);
    return item ? (item.scoring_rule || '') : '';
  };

  const showRule = (rule) => {
    if (rule) setPopover({ visible: true, rule });
  };

  // ===== 事件处理 =====
  const handleEducationChange = (value) => {
    if (housekeeper) {
      setHousekeeper({ ...housekeeper, education: value });
      // 重新计算学历分数
      const eduScore = calcEducationScore(value, roleConfig.education_scoring_rule);
      setMainScores(prev => ({ ...prev, education_score: eduScore }));
      setAssessment(prev => ({ ...prev, education_score: eduScore }));
    }
  };

  const handleMedicalProfession = (val) => {
    setMedicalProfession(val);
    setMainScores(prev => ({ ...prev, education_bonus: val }));
  };

  const handleWorkExpChange = (value) => {
    setMainScores(prev => ({ ...prev, work_experience_score: parseFloat(value) || 0 }));
  };

  const handleCertificateRadio = (detailId, val, maxScore) => {
    const score = val === 1 ? parseFloat(maxScore) : 0;
    setDetailScores(prev => ({ ...prev, [detailId]: score }));
    setCertificateRadios(prev => ({ ...prev, [detailId]: val }));
  };

  const handleInterviewOption = (field, idx) => {
    setInterviewSelections(prev => ({ ...prev, [field]: idx }));
    const opts = roleConfig?.interview_options?.[field];
    if (idx >= 0 && opts && opts[idx]) {
      setMainScores(prev => ({ ...prev, [field + '_score']: opts[idx].score }));
    }
  };

  const handleDetailScoreChange = (detailId, value) => {
    setDetailScores(prev => ({ ...prev, [detailId]: parseFloat(value) || 0 }));
  };

  const handleSpecialSkillChange = (value) => {
    setMainScores(prev => ({ ...prev, special_skill_score: parseFloat(value) || 0 }));
  };

  const handleMixedWorkChange = (value) => {
    setMainScores(prev => ({ ...prev, mixed_work_score: parseFloat(value) || 0 }));
  };

  // ===== 保存 =====
  const handleSave = async () => {
    try {
      const detailUpdates = details.map(d => ({ id: d.id, score: detailScores[d.id] || 0 }));
      const mainScoresData = {
        ...mainScores,
        medical_profession: medicalProfession,
      };
      await updateAssessmentReview(id, { details: detailUpdates, main_scores: mainScoresData });
      alert('已保存');
    } catch (e) { console.error('Save failed', e); alert('保存失败'); }
  };

  const handleCalculate = async () => {
    try {
      const detailUpdates = details.map(d => ({ id: d.id, score: detailScores[d.id] || 0 }));
      const mainScoresData = {
        ...mainScores,
        medical_profession: medicalProfession,
      };
      await updateAssessmentReview(id, {
        details: detailUpdates,
        main_scores: mainScoresData,
      });
      alert('计算完成，总分已更新！');
      loadData();
    } catch (e) { console.error('Calculate failed', e); alert('计算失败'); }
  };

  if (loading) return <div className="review-loading">加载中...</div>;

  // 如果角色配置未加载，显示加载中
  if (!roleConfig) return <div className="review-loading">加载中...</div>;

  // ===== 分组细项 =====
  const groupedDetails = {};
  details.forEach(d => {
    if (!groupedDetails[d.category]) groupedDetails[d.category] = [];
    groupedDetails[d.category].push(d);
  });

  // ===== 计算总分（统一使用纯函数）=====
  const currentTotal = calculateTotalFromState(mainScores, detailScores, groupedDetails, roleConfig);

  // 证书类小计（用于渲染）
  const certCap = roleConfig?.certificate_cap || 3;
  const certSum = (groupedDetails.certificate || []).reduce((sum, d) => sum + (parseFloat(detailScores[d.id]) || 0), 0);
  const certTotal = Math.min(certSum, certCap);

  const isPassed = currentTotal >= (roleConfig?.pass_score || 60);

  // ===== 辅助函数 =====
  const getCatTotal = (cat) => {
    const items = groupedDetails[cat] || [];
    return items.reduce((sum, d) => sum + (parseFloat(detailScores[d.id]) || 0), 0);
  };

  // ===== 渲染技能类卡片 =====
  const renderSkillCategory = (catKey) => {
    const items = groupedDetails[catKey];
    if (!items || items.length === 0) return null;
    const catTotal = getCatTotal(catKey);
    const categoryLabels = roleConfig?.category_labels || {};
    const catLabel = categoryLabels[catKey] || catKey;

    return (
      <div className="review-card" key={catKey}>
        <div className="card-header">
          <span className="dot-orange"></span>
          {catLabel}
          <span className="cat-total">小计：{catTotal}/{items.reduce((s, d) => s + (parseFloat(d.max_score) || 0), 0)}</span>
        </div>
        {items.map(d => {
          // 优先使用后端返回的 scoring_rule，否则回退到 criteriaMap 查找
          const rule = d.scoring_rule || getScoringRule(d.category, d.item_order);
          return (
            <div key={d.id}>
              <div className="score-row">
                <span className="score-label-detail">
                  No.{d.item_order} {d.item_name}
                  {rule && <span className="help-icon" onClick={() => showRule(rule)}>?</span>}
                </span>

                <select className="score-select-small" value={detailScores[d.id] || 0}
                  onChange={(e) => handleDetailScoreChange(d.id, e.target.value)}>
                  {(d.select_options ? JSON.parse(d.select_options) : ['-', '0', '0.5', '1', '1.5', '2']).map(o =>
                    <option key={o} value={o}>{o}</option>
                  )}
                </select>
                <span className="score-max">/{d.max_score}</span>
              </div>
              {d.ai_answer ? (
                <div className="ai-answer-box">
                  <span className="ai-answer-label">阿姨回答：</span>
                  <span className="ai-answer-text">{d.ai_answer}</span>
                </div>
              ) : (
                <div className="ai-answer-box ai-answer-empty">
                  <span className="ai-answer-label">（未提及）</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const pageTitle = roleConfig.page_title || '内部测评';
  const personalMax = roleConfig.personal_qualification_max;
  const workExpMax = roleConfig.work_experience_max;
  const interviewMax = roleConfig.interview_performance_max;
  const totalMax = roleConfig.total_max_score;
  const specialSkillCriteria = roleConfig.special_skill || {};
  const mixedWorkCriteria = roleConfig.mixed_work || {};
  const specialSkillMax = specialSkillCriteria.max_score || 0;
  const mixedWorkMax = mixedWorkCriteria.max_score || 0;
  const interviewOpts = roleConfig.interview_options || {};
  const interviewMaxMap = roleConfig.interview_max_map || {};
  const workExpOpts = roleConfig.work_exp_options || [];
  const skillCats = roleConfig.skill_categories || [];
  const specialSkillLabel = specialSkillCriteria.item_name || '';
  const mixedWorkLabel = mixedWorkCriteria.item_name || '';
  const specialSkillOptions = specialSkillCriteria.select_options || [];
  const mixedWorkOptions = mixedWorkCriteria.select_options || [];

  // 动态生成升值项选项
  const generateScoreOptions = (max) => {
    const opts = ['-'];
    for (let i = 1; i <= max; i++) {
      opts.push(String(i));
    }
    return opts;
  };

  return (
    <div className="review-page">
      <div className="review-back-header">
        <button className="review-back-btn" onClick={() => navigateTo('detail', { id })}>← 返回</button>
      </div>
      <div className="review-title">{pageTitle}</div>

      {/* ===== 个人资历类 ===== */}
      <div className="review-card">
        <div className="card-header"><span className="dot-orange"></span>个人资历类（{personalMax}分）</div>

        {/* 年龄 - 自动计算 */}
        <div className="score-row">
          <span className="score-label">年龄：{housekeeper.age || '-'} 岁</span>
          <span className="score-display">{parseFloat(assessment.age_score) || 0}分</span>
          <span className="help-icon" onClick={() => showRule(getPersonalRule('age'))}>?</span>
        </div>

        {/* 学历 - 自动计算 */}
        <div className="score-row">
          <span className="score-label">学历：</span>
          <select className="score-select" value={housekeeper.education || ''} onChange={(e) => handleEducationChange(e.target.value)}>
            <option value="">请选择</option>
            {(roleConfig.education_options || []).map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <span className="score-display">{parseFloat(mainScores.education_score) || 0}分</span>
          <span className="help-icon" onClick={() => showRule(getPersonalRule('education'))}>?</span>
        </div>

        {/* 专业加分 - 单选按钮（是/否） */}
        {(() => {
          const medicalItem = criteriaMap['personal_qualification']?.find(i => i.item_order === 3);
          if (!medicalItem) return null;
          const options = medicalItem.select_options ? JSON.parse(medicalItem.select_options) : ['是(5分)', '否(0分)'];
          return (
            <div className="score-row">
              <span className="score-label">{medicalItem.item_name}</span>
              <div className="radio-group">
                {options.map((opt, idx) => {
                  const score = parseInt(opt.match(/\d+/)?.[0] || 0);
                  const label = opt.replace(/\(\d+分\)/, '').trim();
                  return (
                    <label key={idx} className="radio-item">
                      <input
                        type="radio"
                        name="medical_profession"
                        value={score}
                        checked={medicalProfession === score}
                        onChange={() => handleMedicalProfession(score)}
                      /> {label}
                    </label>
                  );
                })}
              </div>
              <span className="score-display">{medicalProfession || 0}分</span>
              <span className="help-icon" onClick={() => showRule(medicalItem.scoring_rule || '')}>?</span>
            </div>
          );
        })()}
      </div>

      {/* ===== 工作经验类 ===== */}
      <div className="review-card">
        <div className="card-header"><span className="dot-orange"></span>工作经验类（{workExpMax}分）</div>
        <div className="score-row">
          <span className="score-label">工作经验</span>
          <select className="score-select" value={mainScores.work_experience_score || 0} onChange={(e) => handleWorkExpChange(e.target.value)}>
            <option value={0}>请选择</option>
            {workExpOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <span className="score-display">{mainScores.work_experience_score || '-'}分</span>
        </div>
      </div>

      {/* ===== 证书类 ===== */}
      {groupedDetails.certificate && groupedDetails.certificate.length > 0 && (
        <div className="review-card">
          <div className="card-header">
            <span className="dot-orange"></span>
            {roleConfig.category_labels?.certificate || '证书类（3分）'}
            <span className="cat-total">小计：{certTotal}/{certCap}（上限{certCap}分）</span>
          </div>
          {groupedDetails.certificate.map(d => (
            <div className="score-row" key={d.id}>
              <span className="score-label">{d.item_name}</span>
              {d.score_type === 'required' ? (
                <span className="score-display required-tag">必备</span>
              ) : (
                <>
                  <div className="radio-group">
                    <label className="radio-item">
                      <input type="radio" name={`cert-${d.id}`} checked={certificateRadios[d.id] === 1}
                        onChange={() => handleCertificateRadio(d.id, 1, d.max_score)} /> 有
                    </label>
                    <label className="radio-item">
                      <input type="radio" name={`cert-${d.id}`} checked={certificateRadios[d.id] === 0}
                        onChange={() => handleCertificateRadio(d.id, 0, d.max_score)} /> 无
                    </label>
                  </div>
                  <span className="score-display">{certificateRadios[d.id] === 1 ? d.max_score + '分' : '0分'}</span>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ===== 面试表现-服务素养 ===== */}
      <div className="review-card">
        <div className="card-header"><span className="dot-orange"></span>面试表现-服务素养（{interviewMax}分）</div>
        {Object.keys(interviewOpts).length > 0 ? (
          Object.keys(interviewOpts).map(field => {
            const opts = interviewOpts[field];
            const maxScore = interviewMaxMap[field] || 2;
            const selIdx = interviewSelections[field];
            const labelMap = { appearance: '仪容仪表', etiquette: '礼貌礼节', communication: '沟通能力', logic: '条理逻辑', comprehensive: '综合能力' };
            const rule = getScoringRule('interview_performance', Object.keys(interviewOpts).indexOf(field) + 1);
            return (
              <div className="score-row" key={field}>
                <span className="score-label">
                  {labelMap[field]}
                  {rule && <span className="help-icon" onClick={() => showRule(rule)}>?</span>}
                </span>
                <select className="score-select-wide" value={selIdx} onChange={(e) => handleInterviewOption(field, parseInt(e.target.value))}>
                  <option value={-1}>请选择</option>
                  {opts && opts.map((o, i) => <option key={i} value={i}>{o.label}（{o.score}分）</option>)}
                </select>
                <span className="score-display">{selIdx >= 0 && opts && opts[selIdx] ? opts[selIdx].score + '分' : '-'}</span>
                <span className="score-max">/{maxScore}</span>
              </div>
            );
          })
        ) : (
          <div className="score-row">
            <span className="score-label">暂无面试表现配置</span>
          </div>
        )}
      </div>

      {/* ===== 技能类各卡片 ===== */}
      {skillCats.map(cat => renderSkillCategory(cat.key))}

      {/* ===== 升值项-特殊技能 ===== */}
      <div className="review-card">
        <div className="card-header"><span className="dot-orange"></span>{specialSkillLabel}</div>
        <div className="score-row">
          <select className="score-select" value={mainScores.special_skill_score || 0} onChange={(e) => handleSpecialSkillChange(e.target.value)}>
            {(specialSkillOptions.length > 0 ? specialSkillOptions : generateScoreOptions(specialSkillMax)).map(o => <option key={o} value={o === '-' ? 0 : o}>{o}</option>)}
          </select>
          <span className="score-max">/{specialSkillMax}分</span>
        </div>
      </div>

      {/* ===== 升值项-混岗/到店实操 ===== */}
      <div className="review-card">
        <div className="card-header"><span className="dot-orange"></span>{mixedWorkLabel}</div>
        <div className="score-row">
          <select className="score-select" value={mainScores.mixed_work_score || 0} onChange={(e) => handleMixedWorkChange(e.target.value)}>
            {(mixedWorkOptions.length > 0 ? mixedWorkOptions : generateScoreOptions(mixedWorkMax)).map(o => <option key={o} value={o === '-' ? 0 : o}>{o}</option>)}
          </select>
          <span className="score-max">/{mixedWorkMax}分</span>
        </div>
      </div>

      {/* ===== 底部固定栏 ===== */}
      <div className="review-bottom-bar">
        <div className="bottom-left">
          <button className="save-btn" onClick={handleSave}>保存</button>
          <button className="calculate-btn" onClick={handleCalculate}>计算</button>
        </div>
        <div className="bottom-right">
          <span className="total-display">
            总分：{currentTotal}
          </span>
          <span className={isPassed ? 'pass-tag' : 'fail-tag'}>{isPassed ? ' 合格' : ' 不合格'}</span>
        </div>
        <button className="back-home-btn" onClick={() => navigateTo && navigateTo('home')}>回主页</button>
      </div>

      {/* ===== 评分规则浮窗 ===== */}
      <ScoringRulePopover visible={popover.visible} rule={popover.rule} onClose={() => setPopover({ visible: false, rule: '' })} />
    </div>
  );
}
